import dayjs, { Dayjs } from "dayjs";
import { toOpsUtcBound } from "../OpsTat/OpsTatFilters";

export type RedemptionSource = "all" | "individual" | "onelink";

export type RedemptionProductView = "lines" | "pack" | "variant" | "city";

export type RedemptionGroupBy = "product" | "city";

export type RedemptionSortDir = "asc" | "desc";

export interface RedemptionSort {
  column: string;
  dir: RedemptionSortDir;
}

/** JSON body for redemption summary, drilldown, and variant matrix. */
export interface RedemptionFilters {
  start_date?: string;
  end_date?: string;
  order_id?: number[];
  customer_id?: number[];
  source?: RedemptionSource;
  page?: number;
  page_size?: number;
  sort?: RedemptionSort;
  format?: "json" | "csv";
  view?: RedemptionProductView;
  group_by?: RedemptionGroupBy;
}

export interface RedemptionAppliedFilters {
  start_date: Dayjs;
  end_date: Dayjs;
  order_id: number[];
  customer_id: number[];
  source: RedemptionSource;
}

export const REDEMPTION_PAGE_SIZE = 25;
export const REDEMPTION_MAX_PAGE_SIZE = 100;

export const REDEMPTION_LINES_SORT = ["redeemed_date", "units", "qty"] as const;
export const REDEMPTION_AGG_SORT = ["units", "coupons"] as const;
export const REDEMPTION_JOURNEY_SORT = [
  "created_date",
  "sent_date",
  "redeemed_date",
  "expiry_date",
  "coupon_id",
  "status",
] as const;

export const defaultJourneySort = (): RedemptionSort => ({
  column: "created_date",
  dir: "desc",
});

export const defaultRedemptionAppliedFilters = (): RedemptionAppliedFilters => ({
  start_date: dayjs().subtract(29, "day").startOf("day"),
  end_date: dayjs().endOf("day"),
  order_id: [],
  customer_id: [],
  source: "individual",
});

export const defaultSortForView = (
  view: RedemptionProductView,
): RedemptionSort => {
  if (view === "lines") {
    return { column: "redeemed_date", dir: "desc" };
  }
  return { column: "units", dir: "desc" };
};

export const sortWhitelistForView = (
  view: RedemptionProductView,
): readonly string[] => {
  if (view === "lines") {
    return REDEMPTION_LINES_SORT;
  }
  return REDEMPTION_AGG_SORT;
};

export const clampRedemptionPageSize = (pageSize?: number): number => {
  const value = pageSize ?? REDEMPTION_PAGE_SIZE;
  if (!Number.isFinite(value)) {
    return REDEMPTION_PAGE_SIZE;
  }
  return Math.min(REDEMPTION_MAX_PAGE_SIZE, Math.max(1, Math.trunc(value)));
};

/** Order ID and customer each work alone or together. Either one drops the date window. */
export const redemptionIgnoresDate = (
  applied: Pick<RedemptionAppliedFilters, "order_id" | "customer_id">,
): boolean => applied.order_id.length > 0 || applied.customer_id.length > 0;

/** Local calendar days → UTC bounds. End is end of the selected day, not the next day. */
export const toRedemptionFilters = (
  applied: RedemptionAppliedFilters,
): RedemptionFilters => {
  try {
    const body: RedemptionFilters = {
      source: applied.source,
    };

    if (!redemptionIgnoresDate(applied)) {
      body.start_date = toOpsUtcBound(applied.start_date, "start");
      body.end_date = toOpsUtcBound(applied.end_date, "end");
    }
    if (applied.order_id.length > 0) {
      body.order_id = applied.order_id;
    }
    if (applied.customer_id.length > 0) {
      body.customer_id = applied.customer_id;
    }

    return body;
  } catch (error) {
    throw error;
  }
};
